import React, { useRef, useState, useMemo } from "react";
import * as XLSX from "xlsx";
import { appCheckHeader } from "../utils/appCheck";

// PDF extraction goes through the app's secured anthropicProxy Cloud Function
// (auth + rate-limit + spend caps) — never directly to api.anthropic.com
// (which would also be blocked by CORS and require a key in the browser).
const AI_PROXY_URL = (process.env.REACT_APP_AI_PROXY_URL || "").trim();

async function getProxyToken() {
  const { getAuth } = await import("firebase/auth");
  const u = getAuth().currentUser;
  if (!u) throw new Error("You must be signed in to use AI extraction.");
  return u.getIdToken();
}

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS  (dark theme — matches updated ProgrammeSection)
// ─────────────────────────────────────────────────────────────────────────────
const T = {
  bg:         "#0A0E1A",
  bgPanel:    "#0F1422",
  bgSurface:  "#141929",
  bgHover:    "#1A2035",
  border:     "#1E2A42",
  borderBright:"#2A3A5C",
  blue:       "#3B82F6",
  blueDim:    "#1D3461",
  blueSoft:   "#1A2A4A",
  teal:       "#10B981",
  tealDim:    "#064E3B",
  tealSoft:   "#0D2E25",
  amber:      "#F59E0B",
  amberDim:   "#78350F",
  amberSoft:  "#2A1E08",
  red:        "#EF4444",
  redDim:     "#7F1D1D",
  redSoft:    "#2A1010",
  maroon:     "#F87171",
  maroonSoft: "#2D1515",
  purple:     "#A78BFA",
  purpleSoft: "#1E1635",
  purpleDim:  "#3B2D6A",
  green:      "#10B981",
  muted:      "#4B5A7A",
  mutedBright:"#6B7FA8",
  text:       "#E2E8F0",
  textDim:    "#94A3B8",
  white:      "#FFFFFF",
  r6:  "6px", r8:  "8px", r10: "10px",
  r12: "12px", r16: "16px", r20: "20px",
  fontMono: "'JetBrains Mono','Fira Code','Cascadia Code',monospace",
  fontSans: "'DM Sans','Helvetica Neue',sans-serif",
  shadowMd: "0 4px 20px rgba(0,0,0,0.5)",
  shadowLg: "0 8px 40px rgba(0,0,0,0.6)",
};

// ─────────────────────────────────────────────────────────────────────────────
// MS PROJECT DEPENDENCY TYPE MAP
// Type 0 = FF, 1 = FS, 2 = SF, 3 = SS  (MS Project numeric codes)
// ─────────────────────────────────────────────────────────────────────────────
const MSP_DEP_TYPE = { "0": "FF", "1": "FS", "2": "SF", "3": "SS" };

// ─────────────────────────────────────────────────────────────────────────────
// XML PARSER  — MS Project .xml format
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get text content of the FIRST matching child element.
 * e.g. getText(taskEl, "Name") → "Foundations"
 */
function getText(parent, tagName) {
  const el = parent.querySelector(`:scope > ${tagName}`);
  return el ? el.textContent.trim() : "";
}

/**
 * Convert MS Project ISO datetime "2024-08-08T08:00:00" → "2024-08-08"
 */
function mspDateToISO(raw) {
  if (!raw) return "";
  // Already ISO date only
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  // DateTime format
  const m = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : "";
}

/**
 * Convert MS Project duration string "PT80H0M0S" (ISO 8601 duration) or
 * plain integer minutes to calendar days.
 * MS Project stores duration in tenths-of-a-minute internally but
 * in XML exports it as either:
 *   - ISO 8601: "PT8H0M0S", "P5DT0H0M0S", "PT40H0M0S"
 *   - Or a plain number (minutes)
 */
function mspDurationToDays(raw, minutesPerDay = 480) {
  if (!raw && raw !== 0) return "0";
  const s = String(raw).trim();

  // ISO 8601 duration: P[n]DT[n]H[n]M[n]S
  if (s.startsWith("P") || s.startsWith("p")) {
    let days = 0, hours = 0, minutes = 0;
    const dMatch = s.match(/(\d+(?:\.\d+)?)D/i);
    const hMatch = s.match(/(\d+(?:\.\d+)?)H/i);
    const mMatch = s.match(/(\d+(?:\.\d+)?)M/i);
    if (dMatch) days    = parseFloat(dMatch[1]);
    if (hMatch) hours   = parseFloat(hMatch[1]);
    if (mMatch) minutes = parseFloat(mMatch[1]);
    const totalMinutes = days * minutesPerDay + hours * 60 + minutes;
    return String(Math.round(totalMinutes / minutesPerDay));
  }

  // Plain number in tenths of minutes (old MSP format)
  if (/^\d+$/.test(s)) {
    const tenthsPerDay = minutesPerDay * 10;
    return String(Math.round(parseInt(s, 10) / tenthsPerDay));
  }

  // Fallback: try "10 days" style strings
  const match = s.match(/([\d.]+)\s*(days?|wks?|weeks?|hrs?|hours?)/i);
  if (match) {
    const num = parseFloat(match[1]);
    const unit = match[2].toLowerCase();
    if (unit.startsWith("wk") || unit.startsWith("week")) return String(Math.round(num * 7));
    if (unit.startsWith("hr") || unit.startsWith("hour")) return String(Math.round(num / 8));
    return String(Math.round(num));
  }

  return "0";
}

/**
 * Infer discipline from WBS prefix or task name keywords.
 */
function inferDiscipline(wbs, name) {
  const w = String(wbs || "");
  const n = String(name || "").toLowerCase();
  const topLevel = parseInt(w.split(".")[0], 10);
  const byTop = { 1: "P&G", 2: "P&G", 3: "Civil", 4: "Design", 5: "Procurement", 6: "M&E", 7: "Commissioning", 8: "Commissioning", 9: "Additional Scope" };
  if (byTop[topLevel]) return byTop[topLevel];
  if (n.includes("electric")) return "Electrical";
  if (n.includes("hvac") || n.includes("mechanical")) return "M&E";
  if (n.includes("plumb")) return "Plumbing";
  if (n.includes("design") || n.includes("drawing")) return "Design";
  if (n.includes("procure") || n.includes("tender") || n.includes("order")) return "Procurement";
  if (n.includes("commission")) return "Commissioning";
  if (n.includes("structural") || n.includes("steel") || n.includes("concrete")) return "Structural";
  return "Civil";
}

/**
 * Infer area from task name keywords, falling back to parent task name.
 */
function inferAreaFromName(name) {
  const n = String(name || "").toLowerCase();
  if (n.includes("blower")) return "Blowerhouse";
  if (n.includes("substation")) return "Substation";
  if (n.includes("transformer")) return "Transformer Area";
  if (n.includes("generator") || n.includes("genset")) return "Generator Area";
  if (n.includes("reactor a")) return "Reactor A";
  if (n.includes("reactor b")) return "Reactor B";
  if (n.includes("reactor")) return "Reactor";
  if (n.includes("road") || n.includes("paving")) return "Roads";
  if (n.includes("office")) return "Office Area";
  if (n.includes("domestic")) return "Domestic Reactor";
  if (n.includes("industrial")) return "Industrial Reactor";
  return "";
}

/**
 * Parse MS Project XML ArrayBuffer → activity array with full dependency links.
 *
 * MS Project XML structure (simplified):
 * <Project>
 *   <Tasks>
 *     <Task>
 *       <UID>1</UID>
 *       <ID>1</ID>
 *       <WBS>1.1</WBS>
 *       <Name>Task Name</Name>
 *       <Duration>PT80H0M0S</Duration>
 *       <Start>2024-08-08T08:00:00</Start>
 *       <Finish>2024-08-22T17:00:00</Finish>
 *       <BaselineStart>…</BaselineStart>
 *       <BaselineFinish>…</BaselineFinish>
 *       <PercentComplete>25</PercentComplete>
 *       <Critical>1</Critical>
 *       <Milestone>0</Milestone>
 *       <Summary>0</Summary>
 *       <OutlineLevel>2</OutlineLevel>
 *       <Notes>…</Notes>
 *       <PredecessorLink>
 *         <PredecessorUID>3</PredecessorUID>
 *         <Type>1</Type>       ← 0=FF, 1=FS, 2=SF, 3=SS
 *         <LinkLag>0</LinkLag> ← in tenths of minutes
 *       </PredecessorLink>
 *     </Task>
 *   </Tasks>
 * </Project>
 */
function parseMSProjectXML(arrayBuffer) {
  const decoder = new TextDecoder("utf-8");
  const xmlText = decoder.decode(arrayBuffer);

  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlText, "application/xml");

  const parseError = xmlDoc.querySelector("parsererror");
  if (parseError) throw new Error("XML parse error: " + parseError.textContent.slice(0, 200));

  // Detect MS Project XML by checking for <Project> root or <Tasks>
  const projectEl = xmlDoc.querySelector("Project");
  if (!projectEl) throw new Error("This doesn't look like an MS Project XML file — no <Project> root element found.");

  const tasksEl = xmlDoc.querySelector("Tasks");
  if (!tasksEl) throw new Error("No <Tasks> element found in the MS Project XML.");

  const taskEls = Array.from(tasksEl.querySelectorAll(":scope > Task"));
  if (!taskEls.length) throw new Error("No <Task> elements found in the XML.");

  // Get working minutes per day from <MinutesPerDay> if present (default 480 = 8h)
  const mpd = parseInt(getText(projectEl, "MinutesPerDay") || "480", 10);

  // ── Pass 1: Build UID→WBS map and collect all tasks ──────────────────────
  // We need UID→WBS so we can resolve predecessor UIDs into WBS IDs
  const uidToWbs = {};   // uid → wbsStr (used as activityId)
  const rawTasks = [];

  taskEls.forEach((el) => {
    const uid         = getText(el, "UID");
    const id          = getText(el, "ID");
    const wbs         = getText(el, "WBS");
    const name        = getText(el, "Name");
    const duration    = getText(el, "Duration");
    const start       = getText(el, "Start");
    const finish      = getText(el, "Finish");
    const baseStart   = getText(el, "BaselineStart");
    const baseFinish  = getText(el, "BaselineFinish");
    const actStart    = getText(el, "ActualStart");
    const actFinish   = getText(el, "ActualFinish");
    const pctComplete = getText(el, "PercentComplete");
    const critical    = getText(el, "Critical");
    const milestone   = getText(el, "Milestone");
    const summary     = getText(el, "Summary");
    const outlineLevel= getText(el, "OutlineLevel");
    const notes       = getText(el, "Notes");

    // Skip the project-level summary task (UID=0 or ID=0)
    if (uid === "0" || id === "0") return;
    // Skip tasks with no name
    if (!name) return;

    const wbsStr = wbs || id || uid;
    if (uid) uidToWbs[uid] = wbsStr;

    // Collect predecessor links
    const predLinks = Array.from(el.querySelectorAll(":scope > PredecessorLink")).map((pl) => ({
      predUID:  getText(pl, "PredecessorUID"),
      type:     getText(pl, "Type"),     // 0=FF,1=FS,2=SF,3=SS
      lagTenths: getText(pl, "LinkLag"), // tenths of minutes, negative = lead
    }));

    rawTasks.push({
      uid, id, wbs: wbsStr, name, duration, start, finish,
      baseStart, baseFinish, actStart, actFinish,
      pctComplete, critical, milestone, summary, outlineLevel, notes,
      predLinks,
    });
  });

  if (!rawTasks.length) throw new Error("No usable tasks found in the XML (all rows were empty or header rows).");

  // ── Pass 2: Build outline hierarchy for area inference ───────────────────
  // Build a map of outlineLevel → last seen task name at that level,
  // so leaf tasks can inherit their parent's area.
  const outlineParentName = {}; // level → name of last summary at that level

  // ── Pass 3: Convert to activity schema ───────────────────────────────────
  const activities = rawTasks.map((r, i) => {
    const isMilestone = r.milestone === "1" || r.milestone === "true";
    const isSummary   = r.summary   === "1" || r.summary   === "true";
    const durDays     = isMilestone ? "0" : mspDurationToDays(r.duration, mpd);
    const plannedStart  = mspDateToISO(r.start);
    const plannedFinish = isMilestone ? plannedStart : mspDateToISO(r.finish);
    const baseStart     = mspDateToISO(r.baseStart)  || plannedStart;
    const baseFinish    = mspDateToISO(r.baseFinish) || plannedFinish;
    const pct           = Math.min(100, Math.max(0, parseInt(r.pctComplete || "0", 10)));
    const isCritical    = r.critical === "1" || r.critical === "true";

    // Status inference
    let status = "Not Started";
    if (pct === 100)        status = "Completed";
    else if (pct > 0)       status = "In Progress";
    else if (isCritical && plannedStart && plannedStart < new Date().toISOString().slice(0, 10)) {
      // past planned start, zero progress, critical — likely delayed
      status = "Not Started";
    }

    // Discipline & area
    const discipline = inferDiscipline(r.wbs, r.name);
    const level = parseInt(r.outlineLevel || "1", 10);
    if (isSummary) outlineParentName[level] = r.name;
    let area = inferAreaFromName(r.name);
    if (!area) {
      // Walk up outline levels to find a parent with a known area
      for (let l = level - 1; l >= 1; l--) {
        if (outlineParentName[l]) {
          area = inferAreaFromName(outlineParentName[l]);
          if (area) break;
        }
      }
    }

    // Resolve predecessor links
    // Primary predecessor = first FS link (or first link of any type)
    // We store all links; the main UI uses `predecessor` + `dependencyType` + `lagDays`
    // Additional links go into remarks
    let predecessor    = "";
    let dependencyType = "FS";
    let lagDays        = "0";
    const additionalPreds = [];

    r.predLinks.forEach((pl, idx) => {
      const predWbs = uidToWbs[pl.predUID] || pl.predUID;
      const depType = MSP_DEP_TYPE[pl.type] || "FS";
      // Convert lag: tenths of minutes → days (divide by mpd*10)
      const lagMinTenths = parseInt(pl.lagTenths || "0", 10);
      const lagDaysNum   = lagMinTenths !== 0 ? Math.round(lagMinTenths / (mpd * 10)) : 0;

      if (idx === 0) {
        predecessor    = predWbs;
        dependencyType = depType;
        lagDays        = String(lagDaysNum); // preserve positive lag and negative lead values
      } else {
        additionalPreds.push(`${predWbs} (${depType}${lagDaysNum !== 0 ? `, ${lagDaysNum > 0 ? "+" : ""}${lagDaysNum}d` : ""})`);
      }
    });

    // Remarks: notes + additional predecessors + type flags
    const remarkParts = [];
    if (isMilestone) remarkParts.push("Milestone");
    if (isSummary)   remarkParts.push("Summary task");
    if (additionalPreds.length) remarkParts.push(`Additional predecessors: ${additionalPreds.join(", ")}`);
    if (r.notes)     remarkParts.push(r.notes.replace(/\n/g, " ").slice(0, 200));

    return {
      activityId:     r.wbs,
      activityName:   r.name,
      discipline,
      area,
      plannedStart,
      plannedFinish,
      durationDays:   durDays,
      progress:       String(pct),
      status,
      predecessor,
      successor:      "",   // successors are derived from predecessors at CPM time
      dependencyType,
      lagDays,
      critical:       isCritical,
      baselineStart:  baseStart,
      baselineFinish: baseFinish,
      revisedStart:   mspDateToISO(r.actStart)  || "",
      revisedFinish:  mspDateToISO(r.actFinish) || "",
      remarks:        remarkParts.join(" | "),
      // Internal flags for preview table
      _index:         i,
      _isMilestone:   isMilestone,
      _isSummary:     isSummary,
      _outlineLevel:  level,
      _predLinkCount: r.predLinks.length,
    };
  });

  return activities;
}

// ─────────────────────────────────────────────────────────────────────────────
// EXCEL PARSER  (MS Project export with Predecessors column)
// ─────────────────────────────────────────────────────────────────────────────
function parseMSProjectDate(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const stripped = s.replace(/^[A-Za-z]{3,4}\s+/, "");
  const dateOnly = stripped.split(" ")[0];
  const parts = dateOnly.split("/");
  if (parts.length === 3) {
    const m = parts[0].padStart(2, "0");
    const d = parts[1].padStart(2, "0");
    let y = parts[2];
    if (y.length === 2) y = "20" + y;
    return `${y}-${m}-${d}`;
  }
  const dt = new Date(s);
  if (!isNaN(dt.getTime())) return dt.toISOString().slice(0, 10);
  return "";
}

function parseDurationToDays(raw) {
  if (!raw && raw !== 0) return "0";
  const s = String(raw).trim().replace(",", ".");
  const match = s.match(/([\d.]+)\s*(days?|wks?|weeks?|hrs?|hours?|edays?|ewks?)/i);
  if (!match) return "0";
  const num = parseFloat(match[1]);
  const unit = match[2].toLowerCase();
  if (unit.startsWith("wk") || unit.startsWith("week") || unit.startsWith("ewk")) return String(Math.round(num * 7));
  if (unit.startsWith("hr") || unit.startsWith("hour")) return String(Math.round(num / 8));
  return String(Math.round(num));
}

/**
 * Parse MS Project Excel export.
 * Handles the Predecessors column which contains values like:
 *   "3"  "3FS"  "3FS+2 days"  "3,4"  "3 FS +2d"
 */
function parseExcelToActivities(arrayBuffer) {
  const workbook = XLSX.read(arrayBuffer, { type: "array" });
  const sheetName = workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
  if (rows.length < 2) throw new Error("Excel sheet appears empty or has no data rows.");

  const headers = rows[0].map(h => String(h).trim().toLowerCase());
  const col = (name) => {
    const synonyms = {
      wbs:          ["wbs", "wbs code", "id", "task id"],
      name:         ["task name", "name", "activity name", "activity", "description"],
      start:        ["start", "planned start", "early start"],
      finish:       ["finish", "planned finish", "early finish"],
      duration:     ["duration", "dur"],
      critical:     ["critical", "crit"],
      predecessors: ["predecessors", "predecessor", "pred", "depends on", "dependencies"],
      pct:          ["% complete", "percent complete", "% comp", "pct complete", "complete"],
      basestart:    ["baseline start", "bl start", "base start"],
      basefinish:   ["baseline finish", "bl finish", "base finish"],
      notes:        ["notes", "remarks", "comments"],
    };
    for (const syn of (synonyms[name] || [name])) {
      const idx = headers.indexOf(syn);
      if (idx !== -1) return idx;
    }
    return -1;
  };

  const iWbs   = col("wbs");  const iName  = col("name");
  const iStart = col("start"); const iFinish = col("finish");
  const iDur   = col("duration"); const iCrit = col("critical");
  const iPred  = col("predecessors"); const iPct  = col("pct");
  const iBStart= col("basestart"); const iBFinish = col("basefinish");
  const iNotes = col("notes");

  if (iName === -1) throw new Error('Could not find a "Task Name" column in the Excel file.');

  // Build WBS→row index map so we can resolve predecessor IDs
  const rawRows = rows.slice(1)
    .filter(r => r.some(c => c !== "" && c !== null && c !== undefined))
    .map((r, i) => ({
      wbs:      iWbs    >= 0 ? String(r[iWbs]    || "").trim() : "",
      name:     iName   >= 0 ? String(r[iName]   || "").trim() : "",
      start:    iStart  >= 0 ? r[iStart]  : "",
      finish:   iFinish >= 0 ? r[iFinish] : "",
      duration: iDur    >= 0 ? r[iDur]    : "",
      critical: iCrit   >= 0 ? r[iCrit]   : "No",
      predsRaw: iPred   >= 0 ? String(r[iPred] || "").trim() : "",
      pct:      iPct    >= 0 ? r[iPct]    : "0",
      bstart:   iBStart >= 0 ? r[iBStart] : "",
      bfinish:  iBFinish>= 0 ? r[iBFinish]: "",
      notes:    iNotes  >= 0 ? String(r[iNotes] || "").trim() : "",
      _rowIndex: i,
    }));

  /**
   * Parse MS Project predecessor cell.
   * Examples: "3", "3FS", "3FS+2 days", "3,4FS", "3 FS +2d", "10SS-1 day"
   * Returns { predId, depType, lagDays } for the first link.
   */
  function parsePredCell(raw) {
    if (!raw) return { predId: "", depType: "FS", lagDays: "0" };
    // Take only the first predecessor if multiple (comma-separated)
    const first = raw.split(/[,;]/)[0].trim();
    if (!first) return { predId: "", depType: "FS", lagDays: "0" };

    // Match: digits, optional type (FS/FF/SS/SF), optional lag (+/-days/d/wks)
    const m = first.match(/^(\d+)\s*(FS|FF|SS|SF)?\s*([+-]\s*\d+\s*(?:days?|d|wks?|weeks?))?/i);
    if (!m) return { predId: first, depType: "FS", lagDays: "0" };

    const predId  = m[1];
    const depType = m[2] ? m[2].toUpperCase() : "FS";
    let lagDays   = "0";
    if (m[3]) {
      const sign    = m[3].trim().startsWith("-") ? -1 : 1;
      const lagMatch = m[3].match(/(\d+)\s*(days?|d|wks?|weeks?)/i);
      if (lagMatch) {
        const num = parseInt(lagMatch[1], 10);
        const unit = lagMatch[2].toLowerCase();
        const days = (unit.startsWith("wk") || unit.startsWith("week")) ? num * 7 : num;
        lagDays = String(sign * days);
      }
    }
    return { predId, depType, lagDays };
  }

  return rawRows.map((r, i) => {
    const wbsStr    = r.wbs || String(i + 1);
    const startStr  = parseMSProjectDate(r.start);
    const finishStr = parseMSProjectDate(r.finish);
    const durDays   = parseDurationToDays(r.duration);
    const isMilestone = durDays === "0";
    const isCritical = /^yes$/i.test(String(r.critical || "").trim()) || r.critical === true || r.critical === 1;
    const pct       = Math.min(100, Math.max(0, parseInt(String(r.pct || "0").replace("%", ""), 10) || 0));
    const bstart    = parseMSProjectDate(r.bstart)  || startStr;
    const bfinish   = parseMSProjectDate(r.bfinish) || finishStr;
    const { predId, depType, lagDays } = parsePredCell(r.predsRaw);

    const remarkParts = [];
    if (isMilestone) remarkParts.push("Milestone");
    // Store extra predecessors (after first) in remarks
    const extraPreds = r.predsRaw.split(/[,;]/).slice(1).map(s => s.trim()).filter(Boolean);
    if (extraPreds.length) remarkParts.push(`Additional predecessors: ${extraPreds.join(", ")}`);
    if (r.notes) remarkParts.push(r.notes.slice(0, 200));

    let status = "Not Started";
    if (pct === 100) status = "Completed";
    else if (pct > 0) status = "In Progress";

    return {
      activityId:     wbsStr,
      activityName:   r.name,
      discipline:     inferDiscipline(wbsStr, r.name),
      area:           inferAreaFromName(r.name),
      plannedStart:   startStr,
      plannedFinish:  finishStr || (isMilestone ? startStr : ""),
      durationDays:   durDays,
      progress:       String(pct),
      status,
      predecessor:    predId,
      successor:      "",
      dependencyType: depType,
      lagDays,
      critical:       isCritical,
      baselineStart:  bstart,
      baselineFinish: bfinish,
      revisedStart:   "",
      revisedFinish:  "",
      remarks:        remarkParts.join(" | "),
      _index:         i,
      _isMilestone:   isMilestone,
      _isSummary:     false,
      _outlineLevel:  1,
      _predLinkCount: r.predsRaw ? r.predsRaw.split(/[,;]/).filter(Boolean).length : 0,
    };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// PDF  (unchanged — Claude AI extraction)
// ─────────────────────────────────────────────────────────────────────────────
const EXTRACTION_SYSTEM_PROMPT = `You are a specialist contract administrator and programme analyst. 
You will receive a construction project programme in PDF format (typically exported from MS Project or Primavera).

Extract ALL programme activities and return ONLY a valid JSON array. No preamble, no markdown fences, no explanation — just the raw JSON array.

Each object in the array must have these fields:
{
  "activityId":     string,   // WBS number or task ID (e.g. "3.2.1")
  "activityName":   string,   // Full task name
  "discipline":     string,   // Inferred from WBS or name: "Civil", "Electrical", "M&E", "Structural", "P&G", "Design", "Procurement", "Commissioning", etc.
  "area":           string,   // Building or zone from the task name e.g. "Blowerhouse", "Substation", "Reactor A", "Roads"
  "plannedStart":   string,   // ISO date YYYY-MM-DD. Empty string if missing.
  "plannedFinish":  string,   // ISO date YYYY-MM-DD. Empty if not determinable.
  "durationDays":   string,   // Duration in calendar days as a string number. Convert weeks: 1 wk = 7 days. "0" for milestones.
  "progress":       string,   // Percentage complete as string e.g. "25". "0" if not visible.
  "status":         string,   // One of: "Not Started", "In Progress", "Completed", "Delayed"
  "predecessor":    string,   // WBS/activity ID of the PRIMARY predecessor if visible. Empty string if not clear.
  "successor":      string,   // Empty string
  "dependencyType": string,   // "FS", "SS", "FF", or "SF". Default "FS".
  "lagDays":        string,   // Lag in days as string. "0" unless clearly stated.
  "critical":       boolean,  // true if activity is highlighted as Critical (red bars), false otherwise
  "baselineStart":  string,   // Same as plannedStart (treat tendered programme as baseline)
  "baselineFinish": string,   // Same as plannedFinish
  "revisedStart":   string,   // Empty string
  "revisedFinish":  string,   // Empty string
  "remarks":        string    // Any notes, e.g. "Milestone", "Summary task", additional predecessor IDs
}

IMPORTANT RULES:
1. Include ALL rows — summary tasks, milestones, and leaf activities.
2. For milestones, set durationDays to "0" and plannedFinish = plannedStart.
3. Convert all duration units to days. "edays" = elapsed days. "ewks" = elapsed weeks × 7.
4. Infer predecessors from arrows, indentation, or dependency columns. Include the WBS ID of the predecessor.
5. If an activity has multiple predecessors, put the primary one in "predecessor" and list others in "remarks".
6. Return ONLY the JSON array — no other text whatsoever.`;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = () => reject(new Error("File read failed"));
    reader.readAsDataURL(file);
  });
}

function parseActivitiesFromResponse(text) {
  const clean = text.replace(/```json|```/g, "").trim();
  const start = clean.indexOf("[");
  const end   = clean.lastIndexOf("]");
  if (start === -1 || end === -1) throw new Error("No JSON array found in response");
  return JSON.parse(clean.slice(start, end + 1));
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED UTILITIES
// ─────────────────────────────────────────────────────────────────────────────

function buildActivityId(projectId, activityId) {
  const slug = String(activityId || "").replace(/[^a-z0-9.]+/gi, "-").toLowerCase();
  return `prog-${String(projectId || "proj").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${slug || Date.now()}`;
}

/**
 * Given an array of activities with predecessor info, derive successor links.
 * Returns a new array with .successor and ._successorCount fields.
 */
function deriveSuccessorsFromPredecessors(activities) {
  const successorMap = {};

  activities.forEach((a) => {
    const currentId = String(a.activityId || "").trim();
    const primaryPred = String(a.predecessor || "").trim();
    const allPreds = [];

    if (primaryPred) allPreds.push(primaryPred);

    // Also derive successors from any extra predecessor links stored in remarks.
    // Example remark text: "Additional predecessors: 3.1 (FS), 3.2 (SS, 2d lag)"
    const remarks = String(a.remarks || "");
    const extraMatch = remarks.match(/Additional predecessors:\s*([^|]+)/i);
    if (extraMatch?.[1]) {
      extraMatch[1]
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean)
        .forEach((part) => {
          const predId = part.replace(/\s*\([^)]*\).*$/, "").trim();
          if (predId) allPreds.push(predId);
        });
    }

    if (!currentId || !allPreds.length) return;

    allPreds.forEach((predId) => {
      if (!successorMap[predId]) successorMap[predId] = [];
      if (!successorMap[predId].includes(currentId)) {
        successorMap[predId].push(currentId);
      }
    });
  });

  return activities.map((a) => {
    const id = String(a.activityId || "").trim();
    const successors = successorMap[id] || [];

    return {
      ...a,
      successor: successors.join(", "),
      _successorCount: successors.length,
    };
  });
}

function getFileType(file) {
  if (!file) return null;
  const name = file.name.toLowerCase();
  if (name.endsWith(".pdf"))  return "pdf";
  if (name.endsWith(".xml"))  return "xml";
  if (name.endsWith(".xlsx") || name.endsWith(".xlsm") || name.endsWith(".xls")) return "excel";
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED MICRO-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
function Btn({ children, onClick, variant = "primary", size = "md", disabled, type = "button" }) {
  const [hover, setHover] = useState(false);
  const base = {
    border: "none", borderRadius: T.r8, fontWeight: 600, cursor: "pointer",
    fontFamily: T.fontSans, transition: "all 0.15s", lineHeight: 1,
    padding: size === "sm" ? "6px 12px" : size === "lg" ? "12px 24px" : size === "xs" ? "4px 8px" : "9px 16px",
    fontSize: size === "sm" ? 12 : size === "lg" ? 14 : size === "xs" ? 11 : 13,
    opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? "none" : "auto",
    display: "inline-flex", alignItems: "center", gap: 6,
  };
  const variants = {
    primary:   { background: hover ? "#2563EB" : T.blue,        color: "#fff" },
    secondary: { background: hover ? T.bgHover : T.bgSurface,   color: T.text,    border: `1px solid ${T.border}` },
    success:   { background: hover ? "#059669" : T.teal,         color: "#fff" },
    danger:    { background: hover ? "#DC2626" : T.red,          color: "#fff" },
    purple:    { background: hover ? "#7C3AED" : T.purple,       color: "#fff" },
    ghost:     { background: hover ? T.bgHover : "transparent",  color: T.blue,    border: `1px solid ${T.border}` },
    subtle:    { background: hover ? T.bgHover : "transparent",  color: T.mutedBright },
    amber:     { background: hover ? "#D97706" : T.amber,        color: "#fff" },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ ...base, ...(variants[variant] || variants.primary) }}>
      {children}
    </button>
  );
}

function Badge({ children, variant = "default" }) {
  const map = {
    default:  { bg: T.bgSurface,  color: T.mutedBright, border: T.border },
    info:     { bg: T.blueSoft,   color: T.blue,         border: T.blueDim },
    success:  { bg: T.tealSoft,   color: T.teal,         border: T.tealDim },
    warning:  { bg: T.amberSoft,  color: T.amber,        border: T.amberDim },
    danger:   { bg: T.redSoft,    color: T.red,          border: T.redDim },
    purple:   { bg: T.purpleSoft, color: T.purple,       border: T.purpleDim },
    xml:      { bg: "#0D2A1A",    color: "#34D399",      border: "#065F46" },
  };
  const s = map[variant] || map.default;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", padding: "2px 9px",
      borderRadius: 999, fontSize: 11, fontWeight: 700,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      whiteSpace: "nowrap", fontFamily: T.fontSans,
    }}>{children}</span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PROGRESS STEPS  (PDF flow)
// ─────────────────────────────────────────────────────────────────────────────
const PDF_STEPS = [
  { id: "upload",  label: "PDF uploaded",          icon: "📄" },
  { id: "sending", label: "Sending to Claude API", icon: "🔗" },
  { id: "reading", label: "Reading programme",     icon: "👁️" },
  { id: "parsing", label: "Extracting activities", icon: "⚙️" },
  { id: "review",  label: "Ready to review",       icon: "✅" },
];

function ProgressSteps({ currentStep }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "12px 0" }}>
      {PDF_STEPS.map((step, i) => {
        const stepIdx = PDF_STEPS.findIndex(s => s.id === currentStep);
        const done    = i < stepIdx;
        const active  = i === stepIdx;
        return (
          <div key={step.id} style={{ display: "flex", alignItems: "center", gap: 12, opacity: i > stepIdx ? 0.35 : 1, transition: "opacity 0.3s" }}>
            <div style={{
              width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: done ? T.teal : active ? T.blue : T.bgSurface,
              border: `2px solid ${done ? T.teal : active ? T.blue : T.border}`,
              fontSize: done ? 13 : 15, transition: "all 0.3s",
              boxShadow: active ? `0 0 0 4px ${T.blueSoft}` : "none",
            }}>
              {done ? "✓" : step.icon}
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 12, fontWeight: active ? 700 : 500, color: active ? T.blue : done ? T.teal : T.muted }}>
                {step.label}
              </p>
              {active && (
                <p style={{ margin: "2px 0 0", fontSize: 11, color: T.muted }}>
                  {step.id === "sending" ? "Uploading PDF to Anthropic API…"
                   : step.id === "reading" ? "Claude is analysing the programme structure…"
                   : step.id === "parsing" ? "Converting activities to structured data…" : ""}
                </p>
              )}
            </div>
            {active && (
              <div style={{ marginLeft: "auto", width: 16, height: 16, borderRadius: "50%", border: `2px solid ${T.blueSoft}`, borderTopColor: T.blue, animation: "spin 0.8s linear infinite" }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DEPENDENCY LINK STATS  (shown after XML/Excel parse)
// ─────────────────────────────────────────────────────────────────────────────
function LinkStats({ activities }) {
  const withPred   = activities.filter(a => a.predecessor).length;
  const withSucc   = activities.filter(a => a.successor).length;
  const fsLinks    = activities.filter(a => a.predecessor && a.dependencyType === "FS").length;
  const ssLinks    = activities.filter(a => a.predecessor && a.dependencyType === "SS").length;
  const ffLinks    = activities.filter(a => a.predecessor && a.dependencyType === "FF").length;
  const sfLinks    = activities.filter(a => a.predecessor && a.dependencyType === "SF").length;
  const withLag    = activities.filter(a => a.predecessor && Number(a.lagDays) > 0).length;
  const withLead   = activities.filter(a => a.predecessor && Number(a.lagDays) < 0).length;
  const multiPred  = activities.filter(a => a._predLinkCount > 1).length;

  const stats = [
    { label: "Tasks with predecessors", value: withPred,  color: T.blue },
    { label: "Tasks with successors",   value: withSucc,  color: T.teal },
    { label: "FS links",                value: fsLinks,   color: T.teal },
    { label: "SS links",                value: ssLinks,   color: T.amber },
    { label: "FF links",                value: ffLinks,   color: T.purple },
    { label: "SF links",                value: sfLinks,   color: T.mutedBright },
    { label: "With lag days",           value: withLag,   color: T.orange || T.amber },
    { label: "With lead days",          value: withLead,  color: T.red },
    { label: "Multiple predecessors",   value: multiPred, color: T.mutedBright },
  ].filter(s => s.value > 0);

  if (!stats.length) return (
    <div style={{ padding: "10px 14px", borderRadius: T.r8, background: T.amberSoft, border: `1px solid ${T.amberDim}`, fontSize: 12, color: T.amber }}>
      ⚠ No predecessor links found. If your file has a Predecessors column, check that the column header matches "Predecessors" or "Predecessor".
    </div>
  );

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {stats.map(s => (
        <div key={s.label} style={{ padding: "6px 12px", borderRadius: T.r8, background: T.bgSurface, border: `1px solid ${T.border}`, display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 14, fontWeight: 800, color: s.color, fontFamily: T.fontMono }}>{s.value}</span>
          <span style={{ fontSize: 11, color: T.muted }}>{s.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ACTIVITY PREVIEW TABLE
// ─────────────────────────────────────────────────────────────────────────────
const DISC_COLOR = {
  "Civil": T.teal, "Structural": T.blue, "Electrical": T.amber,
  "M&E": T.purple, "Design": "#38BDF8", "Procurement": "#818CF8",
  "Commissioning": T.teal, "P&G": T.mutedBright, "Additional Scope": T.amber,
};

const depTypeColor = { FS: T.teal, SS: T.amber, FF: T.purple, SF: T.red };

function ActivityPreviewTable({ activities, selected, onToggle, onToggleAll, fileType }) {
  const allSelected = activities.length > 0 && selected.size === activities.length;

  const thS = {
    padding: "7px 10px", textAlign: "left", fontSize: 9, fontWeight: 800,
    textTransform: "uppercase", letterSpacing: "0.1em", color: T.muted,
    borderBottom: `1px solid ${T.border}`, background: T.bg,
    whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 1,
  };
  const tdS = { padding: "6px 10px", verticalAlign: "middle", borderBottom: `1px solid ${T.border}` };

  return (
    <div style={{ overflowX: "auto", maxHeight: 480, overflowY: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
        <thead>
          <tr>
            <th style={thS}>
              <input type="checkbox" checked={allSelected} onChange={onToggleAll} style={{ accentColor: T.blue }} />
            </th>
            <th style={thS}>WBS</th>
            <th style={thS}>Activity Name</th>
            <th style={thS}>Discipline</th>
            <th style={thS}>Area</th>
            <th style={thS}>Start</th>
            <th style={thS}>Finish</th>
            <th style={thS}>Dur</th>
            <th style={thS}>%</th>
            <th style={thS}>Pred</th>
            <th style={thS}>Succ</th>
            <th style={thS}>Type</th>
            <th style={thS}>Lag/Lead</th>
            <th style={thS}>Critical</th>
            <th style={thS}>Status</th>
          </tr>
        </thead>
        <tbody>
          {activities.map((a, i) => {
            const idx        = a._index !== undefined ? a._index : i;
            const isSelected = selected.has(idx);
            const dc         = DISC_COLOR[a.discipline] || T.muted;
            const dtColor    = depTypeColor[a.dependencyType] || T.mutedBright;

            return (
              <tr key={idx} style={{ background: a._isSummary ? T.bgSurface : "transparent", opacity: isSelected ? 1 : 0.35, transition: "opacity 0.15s" }}>
                <td style={tdS}>
                  <input type="checkbox" checked={isSelected} onChange={() => onToggle(idx)} style={{ accentColor: T.blue }} />
                </td>
                <td style={{ ...tdS, fontFamily: T.fontMono, fontWeight: 700, fontSize: 10, color: T.blue }}>
                  {a.activityId || "—"}
                </td>
                <td style={{ ...tdS, maxWidth: 260 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {a._isSummary && <span style={{ fontSize: 9, background: T.blueSoft, color: T.blue, padding: "1px 5px", borderRadius: 99, fontWeight: 700, flexShrink: 0 }}>SUM</span>}
                    {a._isMilestone && <span style={{ fontSize: 9, background: T.amberSoft, color: T.amber, padding: "1px 5px", borderRadius: 99, fontWeight: 700, flexShrink: 0 }}>◆</span>}
                    <span style={{ fontWeight: a._isSummary ? 700 : 400, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.activityName}>
                      {a.activityName || "—"}
                    </span>
                  </div>
                </td>
                <td style={tdS}>
                  <span style={{ display: "inline-flex", padding: "1px 7px", borderRadius: 999, fontSize: 10, fontWeight: 600, background: `${dc}22`, color: dc }}>
                    {a.discipline || "—"}
                  </span>
                </td>
                <td style={{ ...tdS, color: T.muted, fontSize: 11 }}>{a.area || "—"}</td>
                <td style={{ ...tdS, whiteSpace: "nowrap", fontFamily: T.fontMono, fontSize: 10, color: T.textDim }}>{a.plannedStart || "—"}</td>
                <td style={{ ...tdS, whiteSpace: "nowrap", fontFamily: T.fontMono, fontSize: 10, color: T.textDim }}>{a.plannedFinish || "—"}</td>
                <td style={{ ...tdS, fontFamily: T.fontMono, fontSize: 10, color: T.muted, textAlign: "right" }}>
                  {a._isMilestone ? "◆" : `${a.durationDays}d`}
                </td>
                <td style={{ ...tdS, fontFamily: T.fontMono, fontSize: 10, color: Number(a.progress) > 0 ? T.blue : T.muted }}>
                  {a.progress}%
                </td>
                {/* Predecessor */}
                <td style={{ ...tdS, fontFamily: T.fontMono, fontSize: 10, color: a.predecessor ? T.blue : T.muted }}>
                  {a.predecessor || "—"}
                </td>
                {/* Successor */}
                <td style={{ ...tdS, fontFamily: T.fontMono, fontSize: 10, color: a.successor ? T.teal : T.muted }} title={a.successor || ""}>
                  {a.successor || "—"}
                </td>
                {/* Dependency type */}
                <td style={tdS}>
                  {a.predecessor ? (
                    <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 99, background: `${dtColor}22`, color: dtColor, fontFamily: T.fontMono }}>
                      {a.dependencyType}
                    </span>
                  ) : <span style={{ color: T.muted }}>—</span>}
                </td>
                {/* Lag / Lead */}
                <td style={{ ...tdS, fontFamily: T.fontMono, fontSize: 10, color: Number(a.lagDays) !== 0 ? T.amber : T.muted }}>
                  {a.predecessor && Number(a.lagDays) !== 0
                    ? `${Number(a.lagDays) > 0 ? "+" : ""}${a.lagDays}d`
                    : "—"}
                </td>
                {/* Critical */}
                <td style={tdS}>
                  {a.critical
                    ? <span style={{ fontSize: 10, background: T.maroonSoft, color: T.maroon, padding: "1px 6px", borderRadius: 99, fontWeight: 700 }}>CRIT</span>
                    : <span style={{ color: T.muted }}>—</span>}
                </td>
                {/* Status */}
                <td style={tdS}>
                  <span style={{
                    display: "inline-flex", padding: "1px 7px", borderRadius: 999, fontSize: 10, fontWeight: 600,
                    background: a.status === "Completed" ? T.tealSoft : a.status === "In Progress" ? T.blueSoft : T.bgSurface,
                    color: a.status === "Completed" ? T.teal : a.status === "In Progress" ? T.blue : T.muted,
                  }}>{a.status}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function ProgrammeAIImporter({ currentProjectId, onImportActivities, onClose }) {
  const fileInputRef = useRef(null);

  const [file,             setFile]            = useState(null);
  const [fileType,         setFileType]        = useState(null); // "xml" | "excel" | "pdf"
  const [step,             setStep]            = useState("idle");
  const [extracted,        setExtracted]       = useState([]);
  const [selectedIndices,  setSelectedIndices] = useState(new Set());
  const [errorMessage,     setErrorMessage]    = useState("");
  const [importResult,     setImportResult]    = useState(null);
  const [filterDiscipline, setFilterDiscipline]= useState("All");
  const [filterCritical,   setFilterCritical]  = useState("All");
  const [filterType,       setFilterType]      = useState("All"); // All | Summary | Milestone | Leaf
  const [searchText,       setSearchText]      = useState("");
  const [isDragging,       setIsDragging]      = useState(false);

  // ── File selection ─────────────────────────────────────────────────────────
  function handleFileChange(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    acceptFile(f);
  }
  function handleDrop(e) {
    e.preventDefault(); setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (!f) return;
    acceptFile(f);
  }
  function acceptFile(f) {
    const ft = getFileType(f);
    if (!ft) { setErrorMessage("Please select a .xml, .xlsx, or .pdf file."); return; }
    setFile(f); setFileType(ft); setStep("idle");
    setExtracted([]); setSelectedIndices(new Set());
    setErrorMessage(""); setImportResult(null);
  }

  // ── Extract dispatcher ─────────────────────────────────────────────────────
  async function handleExtract() {
    if (!file) return;
    setErrorMessage("");
    if (fileType === "xml")   return handleExtractXML();
    if (fileType === "excel") return handleExtractExcel();
    return handleExtractPDF();
  }

  // ── XML extraction (fully client-side) ────────────────────────────────────
  async function handleExtractXML() {
    setStep("upload");
    try {
      const arrayBuffer = await file.arrayBuffer();
      setStep("parsing");
      const activities = parseMSProjectXML(arrayBuffer);
      if (!activities.length) throw new Error("No tasks found in the XML file.");
      finalise(activities);
    } catch (err) {
      setErrorMessage(err.message || "XML parsing failed.");
      setStep("error");
    }
  }

  // ── Excel extraction (client-side) ────────────────────────────────────────
  async function handleExtractExcel() {
    setStep("upload");
    try {
      const arrayBuffer = await file.arrayBuffer();
      setStep("parsing");
      const activities = parseExcelToActivities(arrayBuffer);
      if (!activities.length) throw new Error("No activities found in the Excel file. Check that it has a Task Name column.");
      finalise(activities);
    } catch (err) {
      setErrorMessage(err.message || "Excel parsing failed.");
      setStep("error");
    }
  }

  // ── PDF extraction (Claude API) ────────────────────────────────────────────
  async function handleExtractPDF() {
    setStep("upload");
    try {
      const base64 = await fileToBase64(file);
      setStep("sending");
      if (!AI_PROXY_URL) throw new Error("AI is not configured (REACT_APP_AI_PROXY_URL is unset).");
      const idToken = await getProxyToken();
      const response = await fetch(AI_PROXY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}`, ...(await appCheckHeader()) },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 8000,
          system: EXTRACTION_SYSTEM_PROMPT,
          messages: [{
            role: "user",
            content: [
              { type: "document", source: { type: "base64", media_type: "application/pdf", data: base64 } },
              { type: "text", text: `Extract all programme activities from this construction programme PDF and return them as a JSON array exactly as instructed. Project ID for reference: ${currentProjectId || "unknown"}.` },
            ],
          }],
        }),
      });
      setStep("reading");
      if (!response.ok) { const err = await response.json(); throw new Error(err?.error?.message || `API error ${response.status}`); }
      const data = await response.json();
      const rawText = (data.content || []).filter(b => b.type === "text").map(b => b.text).join("\n");
      setStep("parsing");
      const activities = parseActivitiesFromResponse(rawText);
      const enriched = activities.map((a, i) => ({
        ...a,
        dependencyType: a.dependencyType || "FS",
        lagDays:        String(a.lagDays || "0"),
        progress:       String(a.progress || "0"),
        predecessor:    a.predecessor || "",
        successor:      a.successor || "",
        revisedStart:   "",
        revisedFinish:  "",
        remarks:        a.remarks || "",
        _index:         i,
        _isMilestone:   a.durationDays === "0" || a.durationDays === 0,
        _isSummary:     false,
        _outlineLevel:  1,
        _predLinkCount: a.predecessor ? 1 : 0,
      }));
      finalise(enriched);
    } catch (err) {
      setErrorMessage(err.message || "Extraction failed.");
      setStep("error");
    }
  }

  function finalise(activities) {
    const enrichedActivities = deriveSuccessorsFromPredecessors(activities);

    setExtracted(enrichedActivities);
    setSelectedIndices(new Set(enrichedActivities.map((_, i) => i)));
    setStep("review");
  }

  // ── Selection ──────────────────────────────────────────────────────────────
  function toggleOne(i) {
    setSelectedIndices(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  }
  function toggleAll() {
    const idxs = filteredActivities.map(a => a._index !== undefined ? a._index : 0);
    const allOn = idxs.every(i => selectedIndices.has(i));
    setSelectedIndices(prev => {
      const n = new Set(prev);
      idxs.forEach(i => allOn ? n.delete(i) : n.add(i));
      return n;
    });
  }

  // ── Filtered view ──────────────────────────────────────────────────────────
  const filteredActivities = useMemo(() => extracted.filter(a => {
    if (filterDiscipline !== "All" && a.discipline !== filterDiscipline) return false;
    if (filterCritical === "Critical"    && !a.critical)  return false;
    if (filterCritical === "Non-Critical" && a.critical)  return false;
    if (filterType === "Summary"   && !a._isSummary)   return false;
    if (filterType === "Milestone" && !a._isMilestone)  return false;
    if (filterType === "Leaf"      && (a._isSummary || a._isMilestone)) return false;
    if (filterType === "WithLinks" && !a.predecessor)    return false;
    const hay = [a.activityId, a.activityName, a.area, a.discipline, a.predecessor, a.successor, a.remarks].join(" ").toLowerCase();
    return !searchText.trim() || hay.includes(searchText.toLowerCase());
  }), [extracted, filterDiscipline, filterCritical, filterType, searchText]);

  const disciplines = useMemo(() => [...new Set(extracted.map(a => a.discipline).filter(Boolean))].sort(), [extracted]);
  const selectedActivities = useMemo(() => extracted.filter((a, i) => selectedIndices.has(a._index !== undefined ? a._index : i)), [extracted, selectedIndices]);
  const criticalCount  = selectedActivities.filter(a => a.critical).length;
  const milestoneCount = selectedActivities.filter(a => a._isMilestone).length;
  const linkedCount    = selectedActivities.filter(a => a.predecessor).length;
  const successorCount = selectedActivities.filter(a => a.successor).length;

  // ── Import ─────────────────────────────────────────────────────────────────
  async function handleImport() {
  if (!currentProjectId || !selectedActivities.length) return;

  setStep("importing");
  setErrorMessage("");

  try {
    const payloads = selectedActivities.map(a => {
      const docId = buildActivityId(currentProjectId, a.activityId || a._index);

      return {
        id:             docId,
        activityId:     String(a.activityId || "").trim(),
        activityName:   String(a.activityName || "").trim(),
        discipline:     a.discipline     || "",
        area:           a.area           || "",
        plannedStart:   a.plannedStart   || "",
        plannedFinish:  a.plannedFinish  || "",
        durationDays:   String(a.durationDays || "0"),
        progress:       String(a.progress || "0"),
        status:         a.status         || "Not Started",
        predecessor:    a.predecessor    || "",
        successor:      a.successor || "",
        dependencyType: a.dependencyType || "FS",
        lagDays:        String(a.lagDays || "0"),
        critical:       Boolean(a.critical),
        baselineStart:  a.baselineStart  || a.plannedStart  || "",
        baselineFinish: a.baselineFinish || a.plannedFinish || "",
        revisedStart:   a.revisedStart   || "",
        revisedFinish:  a.revisedFinish  || "",
        remarks:        [a.remarks || "", `Imported from: ${file?.name || "file"}`].filter(Boolean).join(" | "),
      };
    });

    await onImportActivities(payloads);

    setImportResult({
      total:      payloads.length,
      critical:   criticalCount,
      milestones: milestoneCount,
      linked:     linkedCount,
      successors: successorCount,
      fileName:   file?.name || "file",
      source:     fileType === "xml" ? "MS Project XML" : fileType === "excel" ? "Excel" : "PDF (AI)",
    });

    setStep("done");

  } catch (err) {
    console.error("Programme import failed", err);

    const message = err?.code === "permission-denied"
      ? "Firestore blocked this programme import. Your XML parsed correctly, but Firebase rules are preventing the imported activities from being saved. Update your Firestore rules to allow writes to this project's programme activities/version collection."
      : `Programme import failed: ${err?.message || err}`;

    setErrorMessage(message);
    alert(message);
    setStep("error");
  }
}

  // ── Render flags ───────────────────────────────────────────────────────────
  const isExtracting = ["upload","sending","reading","parsing"].includes(step);
  const isReviewing  = step === "review";
  const isImporting  = step === "importing";
  const isDone       = step === "done";
  const isError      = step === "error";

  const fileAccentColor = fileType === "xml" ? T.green : fileType === "excel" ? T.teal : T.purple;
  const fileLabel       = fileType === "xml" ? "MS Project XML" : fileType === "excel" ? "Excel" : "PDF";

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: T.fontSans, color: T.text }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
        input:focus, select:focus { outline: none; border-color: ${T.blue} !important; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: ${T.bg}; }
        ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 3px; }
      `}</style>

      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      <div style={{
        background: T.bgPanel, border: `1px solid ${T.border}`, borderRadius: T.r16,
        padding: "16px 22px", marginBottom: 12,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        borderTop: `3px solid ${T.purple}`,
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: T.text, letterSpacing: "-0.02em", display: "flex", alignItems: "center", gap: 10 }}>
            <span>🗂</span> Programme Importer
          </h2>
          <p style={{ margin: "3px 0 0", fontSize: 12, color: T.muted }}>
            Import from MS Project XML (recommended) · Excel · or PDF (AI-powered)
          </p>
        </div>
        {onClose && <Btn variant="subtle" size="sm" onClick={onClose}>✕ Close</Btn>}
      </div>

      {/* ── STEP 1: FILE DROP ZONE ─────────────────────────────────────────── */}
      <div style={{ background: T.bgPanel, border: `1px solid ${T.border}`, borderRadius: T.r16, overflow: "hidden", marginBottom: 12 }}>
        <div style={{ padding: "12px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 13, fontWeight: 800, color: T.text }}>Step 1 — Select Programme File</h3>
            <p style={{ margin: "2px 0 0", fontSize: 11, color: T.muted }}>
              MS Project XML gives the richest data including all predecessor/successor links, lag days, and dependency types.
            </p>
          </div>
          {file && <Badge variant={fileType === "xml" ? "xml" : fileType === "excel" ? "success" : "purple"}>{fileLabel} — {file.name}</Badge>}
        </div>

        <div style={{ padding: "16px 20px" }}>
          <input ref={fileInputRef} type="file" accept=".xml,.xlsx,.xlsm,.xls,.pdf" onChange={handleFileChange} style={{ display: "none" }} />

          {/* Drop zone */}
          <div
            onClick={() => !isExtracting && fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${isDragging ? T.blue : file ? fileAccentColor : T.border}`,
              borderRadius: T.r12, padding: "28px 24px", textAlign: "center",
              background: isDragging ? T.blueSoft : file ? `${fileAccentColor}11` : T.bgSurface,
              cursor: isExtracting ? "default" : "pointer", transition: "all 0.15s",
            }}
          >
            <div style={{ fontSize: 36, marginBottom: 10 }}>
              {file ? (fileType === "xml" ? "📐" : fileType === "excel" ? "📊" : "📄") : "📂"}
            </div>
            {file ? (
              <>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: fileAccentColor }}>{file.name}</p>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: T.muted }}>
                  {(file.size / 1024).toFixed(0)} KB · {fileLabel} · Click to change
                </p>
              </>
            ) : (
              <>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: T.text }}>Drop file here or click to browse</p>
                <p style={{ margin: "6px 0 0", fontSize: 12, color: T.muted }}>Accepts .xml · .xlsx · .pdf</p>
              </>
            )}
          </div>

          {/* Format guide */}
          <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            {[
              {
                icon: "📐", title: "MS Project XML", color: T.teal, bg: T.tealSoft, border: T.tealDim,
                body: "File → Save As → XML Format (*.xml). Contains every field: WBS, all dependency types, lag days, baseline dates, actuals, progress — fully automatic, no AI needed.",
                badge: "RECOMMENDED",
              },
              {
                icon: "📊", title: "Excel (.xlsx)", color: T.blue, bg: T.blueSoft, border: T.blueDim,
                body: "File → Export → Excel Workbook. Include columns: WBS, Task Name, Start, Finish, Duration, Critical, Predecessors. Predecessor links are parsed automatically.",
              },
              {
                icon: "📄", title: "PDF (AI-powered)", color: T.purple, bg: T.purpleSoft, border: T.purpleDim,
                body: "Export the printed Gantt view as PDF. Claude reads all pages and infers predecessor links from the visual layout. Use as a last resort when XML/Excel aren't available.",
              },
            ].map(f => (
              <div key={f.title} style={{ padding: "10px 14px", borderRadius: T.r8, background: f.bg, border: `1px solid ${f.border}`, fontSize: 11, color: f.color }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: 14 }}>{f.icon}</span>
                  <strong style={{ fontSize: 12 }}>{f.title}</strong>
                  {f.badge && <span style={{ fontSize: 9, background: f.color, color: T.bg, padding: "1px 5px", borderRadius: 99, fontWeight: 800 }}>{f.badge}</span>}
                </div>
                <span style={{ color: `${f.color}CC` }}>{f.body}</span>
              </div>
            ))}
          </div>

          {/* Extract button */}
          {file && !isExtracting && !isReviewing && !isDone && (
            <div style={{ marginTop: 14 }}>
              <Btn size="lg" variant={fileType === "xml" ? "success" : fileType === "excel" ? "primary" : "purple"} onClick={handleExtract}>
                {fileType === "xml" ? "📐 Parse MS Project XML" : fileType === "excel" ? "📊 Parse Excel File" : "🤖 Extract with AI"}
              </Btn>
            </div>
          )}
          {(isReviewing || isDone) && (
            <div style={{ marginTop: 12 }}>
              <Btn variant="secondary" size="sm" onClick={() => { setExtracted([]); setSelectedIndices(new Set()); setStep("idle"); setImportResult(null); }}>
                ↩ Re-extract from this file
              </Btn>
            </div>
          )}
        </div>
      </div>

      {/* ── PARSING PROGRESS ───────────────────────────────────────────────── */}
      {isExtracting && (fileType === "xml" || fileType === "excel") && (
        <div style={{ background: T.bgPanel, border: `1px solid ${T.border}`, borderRadius: T.r16, padding: "20px 24px", marginBottom: 12, borderTop: `3px solid ${T.teal}`, display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0, border: `3px solid ${T.tealSoft}`, borderTopColor: T.teal, animation: "spin 0.8s linear infinite" }} />
          <div>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: T.text }}>Parsing {fileLabel} file…</p>
            <p style={{ margin: "2px 0 0", fontSize: 11, color: T.muted }}>Reading structure and resolving dependency links — this is instant.</p>
          </div>
        </div>
      )}
      {isExtracting && fileType === "pdf" && (
        <div style={{ background: T.bgPanel, border: `1px solid ${T.border}`, borderRadius: T.r16, padding: "20px 24px", marginBottom: 12, borderTop: `3px solid ${T.purple}` }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 800, color: T.text }}>Step 2 — AI Extraction in Progress</h3>
          <p style={{ margin: "0 0 14px", fontSize: 11, color: T.muted }}>Claude is reading the programme PDF and extracting all activities…</p>
          <ProgressSteps currentStep={step} />
        </div>
      )}

      {/* ── ERROR ──────────────────────────────────────────────────────────── */}
      {isError && (
        <div style={{ padding: "16px 20px", borderRadius: T.r12, marginBottom: 12, background: T.redSoft, border: `1px solid ${T.redDim}`, borderLeft: `3px solid ${T.red}` }}>
          <p style={{ margin: "0 0 4px", fontWeight: 700, color: T.red, fontSize: 13 }}>
  ✗ {step === "error" && errorMessage?.toLowerCase().includes("import") ? "Import failed" : "Extraction failed"}
</p>
          <p style={{ margin: "0 0 12px", fontSize: 12, color: T.red }}>{errorMessage}</p>
          <div style={{ display: "flex", gap: 10 }}>
            <Btn variant="danger" size="sm" onClick={handleExtract}>Retry</Btn>
            <Btn variant="secondary" size="sm" onClick={() => { setStep("idle"); setErrorMessage(""); }}>Dismiss</Btn>
          </div>
        </div>
      )}

      {/* ── STEP 2: REVIEW ─────────────────────────────────────────────────── */}
      {isReviewing && extracted.length > 0 && (
        <>
          {/* Source + link stats */}
          <div style={{ background: T.bgPanel, border: `1px solid ${T.border}`, borderRadius: T.r16, padding: "14px 20px", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <Badge variant={fileType === "xml" ? "xml" : fileType === "excel" ? "success" : "purple"}>
                {fileType === "xml" ? "📐 MS Project XML" : fileType === "excel" ? "📊 Excel" : "🤖 AI (PDF)"}
              </Badge>
              <span style={{ fontSize: 12, color: T.muted }}>{extracted.length} activities found in {file?.name}</span>
            </div>

            {/* Dependency link stats */}
            <div style={{ marginBottom: 4 }}>
              <p style={{ margin: "0 0 8px", fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", color: T.muted, textTransform: "uppercase" }}>Dependency Links Detected</p>
              <LinkStats activities={extracted} />
            </div>
          </div>

          {/* KPI cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 10, marginBottom: 12 }}>
            {[
              { label: "Activities",  value: extracted.length,                                        color: T.blue },
              { label: "Selected",    value: selectedActivities.length,                               color: T.teal },
              { label: "Linked",      value: linkedCount,    sub: "with predecessor",                 color: linkedCount > 0 ? T.teal : T.muted },
              { label: "Successors",  value: successorCount, sub: "derived links",                    color: successorCount > 0 ? T.blue : T.muted },
              { label: "Critical",    value: criticalCount,                                           color: criticalCount > 0 ? T.maroon : T.muted },
              { label: "Milestones",  value: milestoneCount,                                          color: T.amber },
            ].map(k => (
              <div key={k.label} style={{ background: T.bgPanel, border: `1px solid ${T.border}`, borderRadius: T.r12, padding: "12px 16px", borderTop: `3px solid ${k.color}` }}>
                <p style={{ margin: 0, fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: T.muted }}>{k.label}</p>
                <p style={{ margin: "4px 0 0", fontSize: 22, fontWeight: 800, color: k.color, fontFamily: T.fontMono }}>{k.value}</p>
                {k.sub && <p style={{ margin: "2px 0 0", fontSize: 10, color: T.muted }}>{k.sub}</p>}
              </div>
            ))}
          </div>

          {/* Review table */}
          <div style={{ background: T.bgPanel, border: `1px solid ${T.border}`, borderRadius: T.r16, overflow: "hidden", marginBottom: 12 }}>
            {/* Table header */}
            <div style={{ padding: "12px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 13, fontWeight: 800, color: T.text }}>Step 2 — Review & Select Activities</h3>
                <p style={{ margin: "2px 0 0", fontSize: 11, color: T.muted }}>Deselect rows you don't want. Pred/Succ/Type/Lag columns show dependency links, including negative lead values such as FS-10d.</p>
              </div>
              <Btn variant="success" size="lg" disabled={selectedActivities.length === 0 || isImporting} onClick={handleImport}>
                {isImporting ? "Importing…" : `Import ${selectedActivities.length} activities →`}
              </Btn>
            </div>

            {/* Filters */}
            <div style={{ padding: "10px 20px", borderBottom: `1px solid ${T.border}`, background: T.bg, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <input type="text" value={searchText} onChange={e => setSearchText(e.target.value)}
                placeholder="Search WBS, name, area, predecessor, successor…"
                style={{ flex: "0 0 220px", border: `1px solid ${T.border}`, borderRadius: T.r8, padding: "6px 10px", fontSize: 12, background: T.bgSurface, color: T.text, fontFamily: T.fontSans }} />
              {[
                { val: filterDiscipline, set: setFilterDiscipline, opts: [["All", "All disciplines"], ...disciplines.map(d => [d, d])] },
                { val: filterCritical,   set: setFilterCritical,   opts: [["All", "All activities"], ["Critical", "Critical only"], ["Non-Critical", "Non-critical only"]] },
                { val: filterType,       set: setFilterType,        opts: [["All", "All types"], ["Leaf", "Leaf tasks only"], ["Summary", "Summary tasks"], ["Milestone", "Milestones only"], ["WithLinks", "Has predecessor"]] },
              ].map((sel, i) => (
                <select key={i} value={sel.val} onChange={e => sel.set(e.target.value)}
                  style={{ border: `1px solid ${T.border}`, borderRadius: T.r8, padding: "6px 10px", fontSize: 12, background: T.bgSurface, color: T.text, fontFamily: T.fontSans }}>
                  {sel.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              ))}
              <span style={{ fontSize: 11, color: T.muted, fontFamily: T.fontMono, marginLeft: "auto" }}>
                {filteredActivities.length}/{extracted.length} shown
              </span>
            </div>

            {/* Table */}
            <ActivityPreviewTable
              activities={filteredActivities}
              selected={selectedIndices}
              onToggle={toggleOne}
              onToggleAll={toggleAll}
              fileType={fileType}
            />
          </div>

          {/* Bottom import bar */}
          <div style={{ background: T.bgPanel, border: `1px solid ${T.border}`, borderRadius: T.r16, padding: "14px 20px", marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <div>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: T.text }}>
                Ready to import {selectedActivities.length} activit{selectedActivities.length !== 1 ? "ies" : "y"}
              </p>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: T.muted }}>
                {linkedCount} with predecessor links · {successorCount} with successors · {criticalCount} critical · {milestoneCount} milestones
              </p>
            </div>
            <Btn variant="success" size="lg" disabled={selectedActivities.length === 0 || isImporting} onClick={handleImport}>
              {isImporting ? "Importing…" : `✓ Import ${selectedActivities.length} Activities`}
            </Btn>
          </div>
        </>
      )}

      {/* ── DONE ───────────────────────────────────────────────────────────── */}
      {isDone && importResult && (
        <div style={{ padding: "22px 24px", borderRadius: T.r16, background: T.tealSoft, border: `1px solid ${T.tealDim}`, marginBottom: 12 }}>
          <p style={{ margin: "0 0 4px", fontWeight: 800, fontSize: 16, color: T.teal }}>✓ Import complete</p>
          <p style={{ margin: "0 0 16px", fontSize: 13, color: `${T.teal}CC` }}>
            {importResult.total} activit{importResult.total !== 1 ? "ies" : "y"} imported from <strong>{importResult.fileName}</strong> ({importResult.source}).
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
            {[
              { label: "Activities",   value: importResult.total },
              { label: "Linked",       value: importResult.linked },
              { label: "Successors",   value: importResult.successors || 0 },
              { label: "Critical",     value: importResult.critical },
              { label: "Milestones",   value: importResult.milestones },
            ].map(m => (
              <div key={m.label} style={{ background: T.bgPanel, borderRadius: T.r10, padding: "10px 16px", border: `1px solid ${T.tealDim}`, minWidth: 90 }}>
                <p style={{ margin: 0, fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", color: T.muted }}>{m.label}</p>
                <p style={{ margin: "2px 0 0", fontSize: 20, fontWeight: 800, color: T.teal, fontFamily: T.fontMono }}>{m.value}</p>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <Btn variant="secondary" onClick={() => { setFile(null); setFileType(null); setStep("idle"); setExtracted([]); setSelectedIndices(new Set()); setImportResult(null); }}>
              Import another file
            </Btn>
            {onClose && <Btn variant="ghost" onClick={onClose}>View Programme →</Btn>}
          </div>
        </div>
      )}
    </div>
  );
}